import { useIsMobile } from '@hooks';
import Modal from '@components/Modal';
import ArrowCircleLeft from '@components/Icons/ArrowCircleLeft';
import { ReactElement, useState } from 'react';
import EditWorkflowForm from '@components/EditWorkflowForm';
import { FullWorkflow, ThisWorkflowStep } from '@components/WorkflowCard/types';
import ActionEditForm from '@components/ActionEditForm';
import useNoodleApi from '@hooks/useNoodleApi';
import getWorkflowById from '@tsClient/workflows/getWorkflowById';
import s from './EditWorkflowModal.module.scss';

type Props = {
  workflow: FullWorkflow;
  onClose: () => void;
  onSave: () => void;
};

const EditWorkflowModal = ({ onClose, onSave, workflow }: Props): ReactElement => {
  const isMobile = useIsMobile();
  const [currentStep, setCurrentStep] = useState<ThisWorkflowStep | null>(null);
  const [currentWorkflow, setCurrentWorkflow] = useState<FullWorkflow>(workflow);

  const { getData: getWorkflowFn } = useNoodleApi(getWorkflowById);

  const setUpdatedWorkflow = async (): Promise<void> => {
    const { data: updatedWorkflow } = await getWorkflowFn({ id: workflow.id });
    if (updatedWorkflow) {
      setCurrentWorkflow(updatedWorkflow);
    }
  };

  const handleActionEdit = (action: ThisWorkflowStep): void => {
    setCurrentStep(action);
  };

  const onFinishEdit = async (): Promise<void> => {
    await setUpdatedWorkflow();
    setCurrentStep(null);
  };

  return (
    <Modal
      hideBottomInset
      isPinnedToBottom={isMobile}
      title={currentStep ? 'Craft Message' : 'Edit Workflow'}
      onClose={currentStep ? () => setCurrentStep(null) : onClose}
      hasCloseOnActions={false}
      icon={currentStep ? <ArrowCircleLeft weight="fill" size={24} color='var(--color-gray-100)' /> : undefined}
    >
      <div className={s.editWorkflow}>
        {currentStep
          ? (
            <ActionEditForm
              action={currentStep}
              onFinishEdit={onFinishEdit}
              workflow={currentWorkflow}
            />
          )
          : (
            <EditWorkflowForm
              onSave={onSave}
              onEditAction={handleActionEdit}
              refetchWorkflow={setUpdatedWorkflow}
              workflow={currentWorkflow}
            />
          )}
      </div>
    </Modal>
  );
};

export default EditWorkflowModal;
